import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { Bean, MoneyPatternType, PercentagePatternType, ValidationRules } from '@dreryk/react-common';
import { InteractiveTable, InteractiveTableCellProps, useInteractiveTableContext } from '@dreryk/react-components';

import { medicalServiceTranslationKeys } from '../../../../../translations';
import { MedicalServiceSettlementType } from '../../../settlement/settlement.types';
import { createMedicalServiceSettlementTypeVisitor } from '../../../settlement/settlement.utils';
import {
   MedicalServicePractitionerBean,
   MedicalServicePractitionerFixedValue,
   MedicalServicePractitionerProportionalValue,
} from '../../practitioner.types';

type MedicalServicePractitionerFixedValueCellProps = InteractiveTableCellProps<MedicalServicePractitionerFixedValue | null>;

const MedicalServicePractitionerFixedValueCell = (
   props: MedicalServicePractitionerFixedValueCellProps
): JSX.Element => {
   const { getCellValue, triggerCell } = useInteractiveTableContext<MedicalServicePractitionerBean>();
   const usingDefaultConfiguration = getCellValue('usingDefaultConfiguration', props.index);
   const individualPrice = getCellValue('individualPrice', props.index);

   const validationRules = useMemo<ValidationRules<Bean>>(() => {
      if (usingDefaultConfiguration) {
         return {};
      }

      return {
         required: true,
         validate: (data) => {
            const value = data as number;
            if (individualPrice && individualPrice < value) {
               return medicalServiceTranslationKeys.shared.fixedValue.cell.validate.max;
            }

            return true;
         },
      };
   }, [individualPrice, usingDefaultConfiguration]);

   useUpdateEffect(() => {
      triggerCell('fixedValue', props.index);
   }, [individualPrice]);

   return (
      <InteractiveTable.MoneyInputCell
         testId={props.testId}
         name={props.name}
         index={props.index}
         validationRules={validationRules}
         defaultValue={props.defaultValue}
         disabled={usingDefaultConfiguration}
         minSize={5}
         maxSize={9}
         min={1}
         max={999999.99}
         pattern={MoneyPatternType.POSITIVE}
      />
   );
};

type MedicalServicePractitionerProportionalValueCellProps = InteractiveTableCellProps<MedicalServicePractitionerProportionalValue | null>;

const MedicalServicePractitionerProportionalValueCell = (
   props: MedicalServicePractitionerProportionalValueCellProps
): JSX.Element => {
   const { getCellValue } = useInteractiveTableContext<MedicalServicePractitionerBean>();
   const usingDefaultConfiguration = getCellValue('usingDefaultConfiguration', props.index);

   const validationRules = useMemo<ValidationRules<Bean>>(() => {
      return {
         required: !usingDefaultConfiguration,
      };
   }, [usingDefaultConfiguration]);

   return (
      <InteractiveTable.PercentageInputCell
         testId={props.testId}
         name={props.name}
         index={props.index}
         validationRules={validationRules}
         defaultValue={props.defaultValue}
         disabled={usingDefaultConfiguration}
         min={1}
         max={100}
         pattern={PercentagePatternType.POSITIVE}
      />
   );
};

type MedicalServicePractitionerSalaryCellProps = Omit<
   InteractiveTableCellProps<MedicalServicePractitionerFixedValue | MedicalServicePractitionerProportionalValue | null>,
   'name' | 'defaultValue'
> & {
   defaultType: MedicalServiceSettlementType;
   defaultFixedValue: number | null;
   defaultProportionalValue: number | null;
};

export const MedicalServicePractitionerSalaryCell = (props: MedicalServicePractitionerSalaryCellProps): JSX.Element => {
   const { getCellValue } = useInteractiveTableContext<MedicalServicePractitionerBean>();
   const type = getCellValue('settlementType', props.index) ?? props.defaultType;

   return createMedicalServiceSettlementTypeVisitor(type)({
      visitNone: () => <div>{'-'}</div>,
      visitFixed: () => (
         <MedicalServicePractitionerFixedValueCell
            testId={'fixedValue'}
            defaultValue={props.defaultFixedValue}
            index={props.index}
            name={'fixedValue'}
         />
      ),
      visitProportional: () => (
         <MedicalServicePractitionerProportionalValueCell
            testId={'proportionalValue'}
            defaultValue={props.defaultProportionalValue}
            index={props.index}
            name={'proportionalValue'}
         />
      ),
   });
};
